import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { RegistrationService } from '@core/http/registration.service';
import { RegionWordPipe } from '@core/pipes/region-word.pipe';
import { RegistroAsentamiento } from '@core/models/Registration';
import { REGEX } from '@shared/utils/regex.constants';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { FileUploadComponent } from '@shared/components/file-upload/file-upload.component';
import { PrivacyPoliciesConsentComponent } from '@shared/components/privacy-policies-consent/privacy-policies-consent.component';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { environment } from '@enviroment/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-public-doctor-registration',
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    NgForOf,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    RegionWordPipe,
    FileUploadComponent,
    PrivacyPoliciesConsentComponent,
    NgxMatIntlTelInputComponent,
  ],
  templateUrl: './public-doctor-registration.component.html',
  styleUrl: './public-doctor-registration.component.scss',
})
export class PublicDoctorRegistrationComponent implements OnInit {
  readonly fallbackImage = 'assets/images/doctor.png';

  @ViewChild(PrivacyPoliciesConsentComponent)
  private policiesConsent?: PrivacyPoliciesConsentComponent;

  userForm: UntypedFormGroup;
  branchForm: UntypedFormGroup;
  doctorForm: UntypedFormGroup;
  assetsForm: UntypedFormGroup;

  doctorSettlements: RegistroAsentamiento[] = [];
  branchSettlements: RegistroAsentamiento[] = [];
  preferredCountries: string[] = ['mx', 'es', 'us'];
  submitting = false;
  createdFolio: string | null = null;

  colegiadoOptions = [
    { id: 1, label: 'MEDICINA GENERAL' },
    { id: 2, label: 'DENTISTA' },
    { id: 3, label: 'PODOLOGIA' },
  ];

  constructor(
    private fb: FormBuilder,
    private registrationService: RegistrationService,
  ) {
    this.userForm = this.fb.group({
      usr: ['', [Validators.required, Validators.pattern(REGEX.user)]],
      email: ['', [Validators.required, Validators.pattern(REGEX.correo)]],
      nombres: ['', [Validators.required, Validators.pattern(REGEX.text)]],
      primerApellido: ['', [Validators.required, Validators.pattern(REGEX.text)]],
      segundoApellido: ['', [Validators.pattern(REGEX.text)]],
      settlementSearch: ['', [Validators.required]],
      domicilio: ['', [Validators.required, Validators.pattern(REGEX.address)]],
      movil: [null, [Validators.required]],
    });

    this.branchForm = this.fb.group({
      nombre: ['', [Validators.required]],
      registroSanitario: ['', [Validators.required, Validators.pattern(REGEX.registroSanitario)]],
      settlementSearch: ['', [Validators.required]],
      idAsentamiento: [null],
      domicilio: ['', [Validators.required]],
      responsable: ['', [Validators.required, Validators.pattern(REGEX.text)]],
      cedulaResponsable: ['', [Validators.required, Validators.pattern(REGEX.cedula)]],
      telefonoResponsable: [null, [Validators.required]],
      emailResponsable: ['', [Validators.required, Validators.pattern(REGEX.correo)]],
    });

    this.doctorForm = this.fb.group({
      cedulaGeneral: ['', [Validators.required, Validators.pattern(REGEX.cedula)]],
      universidad: ['', [Validators.required, Validators.pattern(REGEX.text)]],
      especialidad: ['', [Validators.pattern(REGEX.text)]],
      cedulaEspecialidad: ['', [Validators.pattern(/^\d{0,9}$/)]],
      horario: ['', [Validators.required]],
      colegiado: [1, [Validators.required]],
    });

    this.assetsForm = this.fb.group({
      imagen: [null],
      firma: [null],
      politicasPrivacidad: [[], [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.userForm.get('settlementSearch')?.valueChanges
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe((value: unknown) => {
        if (typeof value === 'string' && REGEX.postCode.test(value)) {
          this.searchSettlements(value, 'doctor');
          return;
        }

        if (typeof value !== 'object') {
          this.doctorSettlements = [];
        }
      });

    this.branchForm.get('settlementSearch')?.valueChanges
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe((value: unknown) => {
        if (typeof value === 'string' && REGEX.postCode.test(value)) {
          this.searchSettlements(value, 'branch');
          return;
        }

        if (typeof value !== 'object') {
          this.branchSettlements = [];
        }
      });

    this.doctorForm.get('cedulaGeneral')?.valueChanges.subscribe(() => this.syncResponsibleFields());
    this.userForm.get('email')?.valueChanges.subscribe(() => this.syncResponsibleFields());
    this.userForm.get('movil')?.valueChanges.subscribe(() => this.syncResponsibleFields());
    this.userForm.get('nombres')?.valueChanges.subscribe(() => this.syncResponsibleFields());
    this.userForm.get('primerApellido')?.valueChanges.subscribe(() => this.syncResponsibleFields());
    this.userForm.get('segundoApellido')?.valueChanges.subscribe(() => this.syncResponsibleFields());

    this.syncResponsibleFields();
  }

  get resolvedPanelImage(): string {
    const raw = this.assetsForm.get('imagen')?.value;
    return raw?.dataUrl || this.fallbackImage;
  }

  displaySettlement(settlement: RegistroAsentamiento | null): string {
    return settlement?.asentamiento ?? '';
  }

  get isSpainRegion(): boolean {
    return String(environment.region ?? '').trim().toUpperCase() === 'ES';
  }

  get branchEntityLabel(): string {
    return this.isSpainRegion ? 'CONSULTORIO' : 'SUCURSAL';
  }

  get branchSectionDescription(): string {
    return this.isSpainRegion
      ? 'INFORMACION GENERAL DEL CONSULTORIO'
      : 'INFORMACION GENERAL DE LA SUCURSAL';
  }

  get responsiblePhoneDisplay(): string {
    return this.toPhoneValue(this.branchForm.get('telefonoResponsable')?.value) ?? '';
  }

  setSelectedDoctorSettlement(value: RegistroAsentamiento): void {
    this.userForm.patchValue({ settlementSearch: value }, { emitEvent: false });
  }

  setSelectedBranchSettlement(value: RegistroAsentamiento): void {
    this.branchForm.patchValue({
      settlementSearch: value,
      idAsentamiento: value.idAsentamiento,
    }, { emitEvent: false });
  }

  onDoctorSettlementBlur(): void {
    const current = this.userForm.get('settlementSearch')?.value;
    if (!current || typeof current !== 'object' || !current.idAsentamiento) {
      this.userForm.patchValue({ settlementSearch: null }, { emitEvent: false });
    }
  }

  onBranchSettlementBlur(): void {
    const current = this.branchForm.get('settlementSearch')?.value;
    if (!current || typeof current !== 'object' || !current.idAsentamiento) {
      this.branchForm.patchValue({ settlementSearch: null, idAsentamiento: null }, { emitEvent: false });
    }
  }

  async submit(): Promise<void> {
    this.createdFolio = null;
    this.userForm.markAllAsTouched();
    this.branchForm.markAllAsTouched();
    this.doctorForm.markAllAsTouched();

    if (this.userForm.invalid || this.branchForm.invalid || this.doctorForm.invalid) {
      return;
    }

    const consentAccepted = await this.policiesConsent?.ensureAccepted();
    if (!consentAccepted) {
      return;
    }

    const doctorSettlement = this.userForm.get('settlementSearch')?.value as RegistroAsentamiento | null;
    if (!doctorSettlement?.idAsentamiento) {
      Swal.fire('COLONIA DEL MEDICO REQUERIDA', 'SELECCIONA UNA COLONIA VALIDA PARA EL MEDICO.', 'warning').then();
      return;
    }

    const branchSettlement = this.branchForm.get('settlementSearch')?.value as RegistroAsentamiento | null;
    if (!branchSettlement?.idAsentamiento) {
      Swal.fire(
        `${this.branchEntityLabel} REQUERIDO`,
        `SELECCIONA UNA COLONIA VALIDA PARA EL ${this.branchEntityLabel}.`,
        'warning'
      ).then();
      return;
    }

    const user = this.userForm.getRawValue();
    const branch = this.branchForm.getRawValue();
    const doctor = this.doctorForm.getRawValue();
    const nombreCompleto = [
      user.nombres,
      user.primerApellido,
      user.segundoApellido,
    ].filter(Boolean).join(' ').trim();
    const movil = this.toPhoneValue(user.movil);

    const payload = {
      usuario: {
        usr: user.usr,
        email: user.email,
        nombres: user.nombres,
        primerApellido: user.primerApellido,
        segundoApellido: user.segundoApellido || null,
        idAsentamiento: doctorSettlement.idAsentamiento,
        domicilio: user.domicilio,
        movil,
      },
      sucursal: {
        nombre: branch.nombre,
        registroSanitario: branch.registroSanitario || null,
        responsable: branch.responsable,
        cedulaResponsable: branch.cedulaResponsable,
        telefonoResponsable: this.toPhoneValue(branch.telefonoResponsable),
        emailResponsable: branch.emailResponsable,
        domicilio: branch.domicilio,
        idAsentamiento: branchSettlement.idAsentamiento,
        status: 'A',
      },
      medico: {
        cedulaGeneral: doctor.cedulaGeneral,
        universidad: doctor.universidad,
        especialidad: doctor.especialidad || null,
        cedulaEspecialidad: doctor.cedulaEspecialidad || null,
        horario: doctor.horario,
        colegiado: Number(doctor.colegiado),
      },
      politicasPrivacidad: this.getAcceptedPolicies(),
      imagen: this.toAssetPayload(this.assetsForm.get('imagen')?.value),
      firma: this.toAssetPayload(this.assetsForm.get('firma')?.value),
    };

    this.submitting = true;
    this.registrationService.registerDoctor(payload).subscribe({
      next: (response) => {
        this.submitting = false;
        this.createdFolio = response.data?.folio ?? null;

        Swal.fire({
          icon: 'success',
          title: 'SOLICITUD ENVIADA',
          html: this.createdFolio
            ? `FOLIO GENERADO: <b>${this.createdFolio}</b><br>SE HA ENVIADO UN CORREO CON EL FOLIO DE LA SOLICITUD.<br>EL REGISTRO QUEDA PENDIENTE DE APROBACION.`
            : (response.message || 'LA SOLICITUD FUE REGISTRADA.'),
        }).then();

        this.userForm.reset();
        this.branchForm.reset();
        this.doctorForm.reset({ colegiado: 1 });
        this.assetsForm.reset({ politicasPrivacidad: [] });
        this.doctorSettlements = [];
        this.branchSettlements = [];
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  private searchSettlements(codigoPostal: string, target: 'doctor' | 'branch'): void {
    this.registrationService.getSettlementsByPostalCode(codigoPostal).subscribe({
      next: (response) => {
        const settlements = [...(response.data ?? [])];
        if (target === 'doctor') {
          this.doctorSettlements = settlements;
          return;
        }

        this.branchSettlements = settlements;
      },
      error: () => {
        if (target === 'doctor') {
          this.doctorSettlements = [];
          return;
        }

        this.branchSettlements = [];
      },
    });
  }

  private toPhoneValue(raw: any): string | null {
    if (!raw) return null;
    if (typeof raw === 'string') return raw.trim() || null;
    return raw.e164Number || raw.e164 || raw.number || null;
  }

  private toAssetPayload(raw: any) {
    if (!raw?.base64) return null;

    return {
      blobBase64: raw.base64,
      contentType: raw.contentType,
      bytesSize: raw.bytesSize,
      widthPx: raw.widthPx ?? null,
      heightPx: raw.heightPx ?? null,
      hashSha256Base64: raw.hashSha256Base64 ?? null,
    };
  }

  private syncResponsibleFields(): void {
    const user = this.userForm.getRawValue();
    const doctor = this.doctorForm.getRawValue();

    this.branchForm.patchValue({
      responsable: [user.nombres, user.primerApellido, user.segundoApellido].filter(Boolean).join(' ').trim(),
      cedulaResponsable: doctor.cedulaGeneral || '',
      telefonoResponsable: user.movil ?? null,
      emailResponsable: user.email || '',
    }, { emitEvent: false });
  }

  private getAcceptedPolicies() {
    return (this.assetsForm.get('politicasPrivacidad')?.value ?? [])
      .filter((item: any) => item?.aceptado)
      .map((item: any) => ({
        idPoliticaPrivacidad: Number(item.idPoliticaPrivacidad),
        aceptado: true,
      }));
  }
}
